import React, { useState, useRef, useEffect } from 'react';
import { useUIActions } from '../store';
import { purchaseOrderApi, vendorApi } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface PurchaseOrder {
  id: string;
  vendor_code: string;
  vendor_name: string;
  upload_date: string;
  total_items: number;
  matched_items: number;
  status: 'pending' | 'processing' | 'completed';
  file_name: string;
  match_rate: number;
}

const PurchaseOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendors, setVendors] = useState<any[]>([]);
  // const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, totalItems: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useUIActions();

  // Load vendors data
  const loadVendors = async () => {
    try {
      const result = await vendorApi.list();
      
      if (result.success && result.data) {
        setVendors(result.data);
      }
    } catch (error) {
      console.error('Failed to load vendors:', error);
    }
  };

  // Load purchase orders from API
  const loadPurchaseOrders = async () => {
    try {
      const result = await purchaseOrderApi.list();
      
      if (result.success && result.data) {
        // 處理分頁資料或直接陣列
        const dataArray = Array.isArray(result.data) ? result.data : (result.data as any).data || [];
        const formattedOrders: PurchaseOrder[] = dataArray.map((po: any) => {
          const vendor = vendors.find(v => v.vendor_code === po.vendor_code);
          return {
            id: po.po_id,
            vendor_code: po.vendor_code,
            vendor_name: vendor ? vendor.vendor_name : `廠商${po.vendor_code}`,
            upload_date: po.created_at,
            total_items: po.total_items,
            matched_items: po.matched_items || 0,
            status: po.status,
            file_name: `${po.po_id}.csv`,
            match_rate: po.total_items > 0 ? ((po.matched_items || 0) / po.total_items) * 100 : 0
          };
        });
        setOrders(formattedOrders);
        
        // 計算統計資料
        // const newStats = {
        //   total: formattedOrders.length,
        //   pending: formattedOrders.filter(o => o.status === 'pending').length,
        //   completed: formattedOrders.filter(o => o.status === 'completed').length,
        //   totalItems: formattedOrders.reduce((sum, o) => sum + o.total_items, 0)
        // };
        // setStats(newStats);
      }
    } catch (error) {
      console.error('Failed to load purchase orders:', error);
      addToast({
        type: 'error',
        title: '載入失敗',
        message: '無法載入採購單資料'
      });
    }
  };

  useEffect(() => {
    loadVendors();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    if (vendors.length > 0) {
      loadPurchaseOrders();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors]);

  // Load purchase order details
  const handleViewDetails = async (order: PurchaseOrder) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
    setDetailLoading(true);
    
    try {
      const result = await purchaseOrderApi.get(order.id);
      
      if (result.success && result.data) {
        setOrderDetails(result.data);
      } else {
        addToast({
          type: 'error',
          title: '載入失敗',
          message: '無法載入採購單詳細資料'
        });
      }
    } catch (error) {
      console.error('Failed to load purchase order details:', error);
      addToast({
        type: 'error',
        title: '載入失敗',
        message: '無法載入採購單詳細資料'
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!validTypes.includes(fileExtension)) {
      addToast({
        type: 'error',
        title: '檔案格式錯誤',
        message: '請上傳 CSV 或 Excel 檔案'
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      addToast({
        type: 'error',
        title: '檔案過大',
        message: '檔案大小不能超過 10MB'
      });
      return;
    }

    uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setLoading(true);
    setUploadProgress(0);
    
    // 實際上傳進度
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + Math.random() * 10;
      });
    }, 300);

    try {
      // 讀取CSV文件內容
      const csvContent = await file.text();
      
      // 調用實際API
      const response = await fetch('http://localhost:8787/api/purchase-orders/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendor_code: '5', // 可以根據需求調整
          csv_data: csvContent
        })
      });

      const result = await response.json();
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        setShowUploadModal(false);
        
        // 重新載入採購單列表
        await loadPurchaseOrders();

        addToast({
          type: 'success',
          title: '上傳成功',
          message: `對帳單已成功上傳！採購單編號：${result.data.po_id}，共 ${result.data.total_items} 個項目`
        });
      } else {
        throw new Error(result.message || '上傳失敗');
      }

    } catch (error) {
      clearInterval(progressInterval);
      addToast({
        type: 'error',
        title: '上傳失敗',
        message: error instanceof Error ? error.message : '檔案上傳過程中發生錯誤'
      });
    } finally {
      setLoading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '待處理';
      case 'processing': return '處理中';
      case 'completed': return '已完成';
      default: return '未知';
    }
  };

  const UploadModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">上傳對帳單</h3>
            <button
              onClick={() => setShowUploadModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <LoadingSpinner size="large" className="mx-auto mb-4" />
              <p className="text-gray-600 mb-2">正在上傳檔案...</p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500 mt-2">{uploadProgress.toFixed(0)}%</p>
            </div>
          ) : (
            <>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
                  dragOver 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-lg font-medium text-gray-900 mb-2">
                  拖拽檔案到此處或點擊選擇
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  支援 CSV、Excel 格式，最大 10MB
                </p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
                >
                  選擇檔案
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                />
              </div>
              
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">檔案格式要求</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• 必須包含商品序號或IMEI欄位</p>
                  <p>• 建議包含商品名稱、型號等資訊</p>
                  <p>• Excel檔案請使用第一個工作表</p>
                  <p>• CSV檔案請使用UTF-8編碼</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const handlePrintInventoryRecord = () => {
    if (!selectedOrder || !orderDetails) return;
    
    // Create print content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>點貨紀錄 - ${selectedOrder.id}</title>
        <style>
          @page { 
            size: A4; 
            margin: 20mm;
          }
          body { 
            font-family: 'Microsoft YaHei', Arial, sans-serif; 
            font-size: 12px; 
            line-height: 1.4;
            color: #333;
            margin: 0;
            padding: 0;
          }
          .header { 
            text-align: center; 
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .company-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .document-title {
            font-size: 16px;
            font-weight: bold;
            color: #666;
          }
          .info-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 20px;
          }
          .info-group {
            border: 1px solid #ddd;
            padding: 10px;
            border-radius: 4px;
          }
          .info-group h4 {
            margin: 0 0 10px 0;
            font-size: 14px;
            font-weight: bold;
            color: #444;
            border-bottom: 1px solid #eee;
            padding-bottom: 5px;
          }
          .info-item {
            margin: 5px 0;
            display: flex;
            justify-content: space-between;
          }
          .info-label {
            font-weight: bold;
            color: #666;
          }
          .table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          .table th, .table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            font-size: 11px;
          }
          .table th {
            background-color: #f5f5f5;
            font-weight: bold;
            text-align: center;
          }
          .table td {
            vertical-align: top;
          }
          .status {
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 10px;
            font-weight: bold;
          }
          .status-completed { background-color: #d4edda; color: #155724; }
          .status-pending { background-color: #fff3cd; color: #856404; }
          .status-processing { background-color: #cce7ff; color: #004085; }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #ddd;
            padding-top: 10px;
          }
          .signature-section {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 40px;
            margin-top: 40px;
            text-align: center;
          }
          .signature-box {
            border-top: 1px solid #333;
            padding-top: 5px;
            font-weight: bold;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">綠途未來股份有限公司</div>
          <div class="document-title">點貨紀錄表</div>
        </div>
        
        <div class="info-section">
          <div class="info-group">
            <h4>基本資訊</h4>
            <div class="info-item">
              <span class="info-label">採購單編號：</span>
              <span>${orderDetails.po_id}</span>
            </div>
            <div class="info-item">
              <span class="info-label">廠商代碼：</span>
              <span>${orderDetails.vendor_code}</span>
            </div>
            <div class="info-item">
              <span class="info-label">狀態：</span>
              <span class="status status-${orderDetails.status}">${getStatusText(orderDetails.status)}</span>
            </div>
            <div class="info-item">
              <span class="info-label">建立日期：</span>
              <span>${new Date(orderDetails.upload_date).toLocaleDateString('zh-TW')}</span>
            </div>
          </div>
          
          <div class="info-group">
            <h4>統計資訊</h4>
            <div class="info-item">
              <span class="info-label">總項目數：</span>
              <span>${orderDetails.total_items}</span>
            </div>
            <div class="info-item">
              <span class="info-label">已掃描：</span>
              <span>${orderDetails.scanned_items || 0}</span>
            </div>
            <div class="info-item">
              <span class="info-label">已匹配：</span>
              <span>${orderDetails.matched_items}</span>
            </div>
            <div class="info-item">
              <span class="info-label">未匹配：</span>
              <span>${orderDetails.unmatched_items || 0}</span>
            </div>
          </div>
        </div>
        
        <table class="table">
          <thead>
            <tr>
              <th width="8%">序號</th>
              <th width="25%">產品名稱</th>
              <th width="20%">序號/IMEI</th>
              <th width="10%">預期數量</th>
              <th width="10%">實收數量</th>
              <th width="12%">單價</th>
              <th width="15%">備註</th>
            </tr>
          </thead>
          <tbody>
            ${orderDetails.items ? orderDetails.items.map((item: any) => `
              <tr>
                <td style="text-align: center;">${item.sequence}</td>
                <td>${item.product_name}</td>
                <td style="font-family: monospace;">${item.serial_number || '-'}</td>
                <td style="text-align: center;">${item.expected_quantity || 1}</td>
                <td style="text-align: center;">${item.received_quantity || 0}</td>
                <td style="text-align: right;">NT$ ${item.unit_price ? item.unit_price.toLocaleString() : '-'}</td>
                <td>${item.notes || '-'}</td>
              </tr>
            `).join('') : ''}
          </tbody>
        </table>
        
        <div class="signature-section">
          <div class="signature-box">點貨人員</div>
          <div class="signature-box">覆核人員</div>
          <div class="signature-box">主管簽核</div>
        </div>
        
        <div class="footer">
          列印時間：${new Date().toLocaleString('zh-TW')} | 
          系統產生：綠途未來點貨系統
        </div>
      </body>
      </html>
    `;
    
    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  const DetailModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              採購單詳細資訊 - {selectedOrder?.id}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrintInventoryRecord}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                列印點貨紀錄
              </button>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {detailLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="large" />
            </div>
          ) : orderDetails ? (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">基本資訊</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-gray-500">採購單編號：</span>{orderDetails.po_id}</div>
                    <div><span className="text-gray-500">廠商代碼：</span>{orderDetails.vendor_code}</div>
                    <div><span className="text-gray-500">狀態：</span>
                      <span className={`ml-1 px-2 py-1 rounded text-xs ${getStatusColor(orderDetails.status)}`}>
                        {getStatusText(orderDetails.status)}
                      </span>
                    </div>
                    <div><span className="text-gray-500">上傳日期：</span>{new Date(orderDetails.upload_date).toLocaleString('zh-TW')}</div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">統計資訊</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-gray-500">總項目數：</span>{orderDetails.total_items}</div>
                    <div><span className="text-gray-500">已掃描：</span>{orderDetails.scanned_items}</div>
                    <div><span className="text-gray-500">已匹配：</span>{orderDetails.matched_items}</div>
                    <div><span className="text-gray-500">未匹配：</span>{orderDetails.unmatched_items}</div>
                  </div>
                </div>
              </div>

              {/* Items List */}
              {orderDetails.items && orderDetails.items.length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">項目明細</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">序號</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">產品名稱</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">序號/IMEI</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">預期數量</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">實收數量</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">單價</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">備註</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {orderDetails.items.map((item: any, index: number) => (
                          <tr key={item.id || index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.sequence}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.product_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.serial_number}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.expected_quantity}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.received_quantity}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">NT$ {item.unit_price?.toLocaleString()}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.notes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-500">無法載入採購單詳細資料</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">採購單管理</h1>
          <p className="mt-1 text-sm text-gray-500">
            管理對帳單和預期商品清單
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              const link = document.createElement('a');
              link.href = `${process.env.NODE_ENV === 'development' ? 'http://localhost:8787' : ''}/api/purchase-orders/template`;
              link.download = 'purchase-order-template.csv';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            下載範本
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            上傳對帳單
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">總對帳單</h3>
              <p className="text-2xl font-semibold text-gray-900">{orders.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">處理中</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {orders.filter(o => o.status === 'processing').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">已完成</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {orders.filter(o => o.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">總商品數</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {orders.reduce((sum, order) => sum + order.total_items, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 搜尋和篩選 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            {/* 搜尋 */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="搜尋採購單編號或廠商..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-80"
              />
            </div>

            {/* 狀態篩選 */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">全部狀態</option>
              <option value="pending">待處理</option>
              <option value="processing">處理中</option>
              <option value="completed">已完成</option>
            </select>
          </div>

          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-500">
              共 {orders.filter(order => {
                const matchesSearch = !searchQuery || 
                  order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  order.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  order.vendor_code.toLowerCase().includes(searchQuery.toLowerCase());
                const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
                return matchesSearch && matchesStatus;
              }).length} 項
            </span>
            <button
              onClick={loadPurchaseOrders}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              刷新
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">對帳單列表</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  對帳單資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  廠商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  商品數量
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  匹配率
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  上傳時間
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.filter(order => {
                const matchesSearch = !searchQuery || 
                  order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  order.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  order.vendor_code.toLowerCase().includes(searchQuery.toLowerCase());
                const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
                return matchesSearch && matchesStatus;
              }).map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{order.id}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">{order.file_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{order.vendor_name}</div>
                    <div className="text-sm text-gray-500">{order.vendor_code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {order.matched_items} / {order.total_items}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                      <div
                        className="bg-primary-600 h-1.5 rounded-full"
                        style={{ width: `${(order.matched_items / order.total_items) * 100}%` }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${
                      order.match_rate >= 95 
                        ? 'text-green-600' 
                        : order.match_rate >= 80 
                        ? 'text-yellow-600' 
                        : 'text-red-600'
                    }`}>
                      {order.match_rate.toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                      {getStatusText(order.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(order.upload_date).toLocaleString('zh-TW')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button 
                        onClick={() => handleViewDetails(order)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        查看詳情
                      </button>
                      <button className="text-gray-400 hover:text-gray-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && <UploadModal />}
      {showDetailModal && <DetailModal />}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          使用說明
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div className="space-y-2">
            <p>• 支援上傳CSV和Excel格式的對帳單</p>
            <p>• 系統會自動解析檔案並進行商品匹配</p>
            <p>• 匹配成功的商品會生成批號供點貨使用</p>
          </div>
          <div className="space-y-2">
            <p>• 未匹配的商品會進入待處理佇列</p>
            <p>• 可以查看詳細的匹配報告和統計</p>
            <p>• 建議定期上傳新的對帳單保持資料同步</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrdersPage;